import { dungeonService } from './DungeonService';
import { PlayerEntity } from '../entities/PlayerEntity';
import { MonsterEntity } from '../entities/MonsterEntity';
import { MONSTER_CONFIGS } from '../data/monsters.config';
import { DungeonEntityType } from '../types/enums';
import {
  SaveData,
  SavedMonster,
  SavedGroundItem,
  SavedActiveBuff,
  SavedPlayer,
} from '../types/save.types';
import { BUFF_REGISTRY } from '../data/buffs.config';
import { ActiveBuff } from '../types/buff.types';
import { DungeonEntity } from '../entities/DungeonEntity';
import { ITile } from '../types/map.types';
import { IRoom } from '../types/map.types';
import { PlacedLocation, MapStyle } from '../types/map.types';
import { mapService } from './MapService';
import { challengeService } from './ChallengeService';
import { netService } from './NetService';
import { reconnectRegistry } from '../net/reconnect';
import { coopDeathHost } from './CoopDeathHost';
import { useGameStore } from '../store/gameSlice';
import { useSocketsStore } from '../store/socketsSlice';
import { useNetSessionStore } from '../store/netSessionSlice';
import type { SavedCoopPlayer } from '../types/save.types';
import type { LoadoutSource } from './LoadoutSourceService';

const SAVE_KEY = 'usagi_save';
const SAVE_VERSION = 12;

// Legacy localStorage cleanup now lives in `src/clearLegacyStorageOnBoot.ts`:
// a version-gated wipe of the whole `usagi` namespace that runs before any
// store loads (this module imports stores, so it can't run early enough).

// ---------------------------------------------------------------------------
// Buff (de)serialization helpers
// ---------------------------------------------------------------------------

function serializeBuffs(entity: DungeonEntity): SavedActiveBuff[] {
  return entity
    .getActiveBuffs()
    .filter((b) => !b.source?.startsWith('spell:'))
    .map((b) => ({
      defId: b.def.id,
      remainingTicks: b.remainingTicks,
    }));
}

/**
 * Rehydrate ActiveBuff from save by looking up `defId` in BUFF_REGISTRY.
 * Buffs whose def has been removed from the registry are silently skipped.
 */
function deserializeBuffs(saved: SavedActiveBuff[]): ActiveBuff[] {
  const out: ActiveBuff[] = [];
  for (const sb of saved) {
    const def = BUFF_REGISTRY[sb.defId];
    if (def) {
      out.push({ def, remainingTicks: sb.remainingTicks });
    }
  }
  return out;
}

function serializePlayer(p: PlayerEntity) {
  return {
    positionX: p.positionX,
    positionY: p.positionY,
    life: p.life,
    maxLife: p.maxLife,
    shield: p.shield,
    stats: { ...p.stats },
    baseStats: { ...p.baseStats },
    direction: p.direction,
    configId: p.configId,
    slots: p.slots.map((s) => ({ ...s })),
    activeBuffs: serializeBuffs(p),
    spellStates: p.getSpellStatesSnapshot(),
  };
}

function buildPlayerFromSavedPlayer(
  sp: SavedPlayer,
  loadoutSource?: LoadoutSource,
): PlayerEntity {
  const player = new PlayerEntity({
    life: sp.life,
    maxLife: sp.maxLife,
    isWalkableOver: false,
    configId: sp.configId,
    x: sp.positionX,
    y: sp.positionY,
    stats: sp.baseStats ?? sp.stats,
    shield: sp.shield,
    loadoutSource,
  });
  if (sp.slots) {
    for (let i = 0; i < sp.slots.length && i < player.slots.length; i++) {
      player.slots[i] = { ...sp.slots[i] };
    }
  }
  player.direction = sp.direction ?? player.direction;
  if (sp.activeBuffs) {
    player.restoreActiveBuffs(deserializeBuffs(sp.activeBuffs));
  }
  // Permanent stat progression (talents + trinket passives) is baked into the
  // serialized baseStats — nothing to re-apply here. Notable-talent companion
  // buffs (`talent-notable:*`) are registry entries and survive restore as-is.
  player.recomputeStats();
  if (sp.spellStates) {
    player.restoreSpellStates(sp.spellStates);
  }
  return player;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class SaveService {
  private static instance: SaveService;

  /** Current map metadata needed for saving. */
  private _mapData: {
    style: MapStyle;
    width: number;
    height: number;
    tiles: ITile[];
    rooms: IRoom[];
    placedLocations: PlacedLocation[];
  } | null = null;

  public static getInstance(): SaveService {
    if (!SaveService.instance) {
      SaveService.instance = new SaveService();
    }
    return SaveService.instance;
  }

  // -- Map data cache (set after generation or load) -----------------------

  setMapData(
    style: MapStyle,
    width: number,
    height: number,
    tiles: ITile[],
    rooms: IRoom[],
    placedLocations: PlacedLocation[],
  ): void {
    this._mapData = { style, width, height, tiles, rooms, placedLocations };
  }

  getMapData() {
    return this._mapData;
  }

  // -- Save ----------------------------------------------------------------

  save(): void {
    // The client never owns the run save (the run is the host's). Guard the
    // generic autosave path so a stray client save can't write usagi_save.
    if (netService.isClient()) {
      return;
    }
    // A room challenge is in progress (doors sealed, wave alive). Persisting now
    // would save the sealed map + half-fought wave; quitting must "not count",
    // so we skip autosave until the challenge clears or the player escapes. The
    // last persisted state is the pristine room from before activation.
    if (challengeService.isActive()) {
      return;
    }
    if (!this._mapData) {
      return;
    }

    const player = dungeonService.getPlayer();
    if (!player) {
      return;
    }

    const monsters: SavedMonster[] = dungeonService
      .getEntities()
      .filter((e): e is MonsterEntity => e.type === DungeonEntityType.MONSTER)
      .map((m) => ({
        entityClass: m.configId,
        positionX: m.positionX,
        positionY: m.positionY,
        life: m.life,
        maxLife: m.maxLife,
        shield: m.shield,
        stats: { ...m.stats },
        baseStats: { ...m.baseStats },
        direction: m.direction,
        configId: m.configId,
        level: m.level,
        aiState: {
          behaviorState: m.aiState.behaviorState,
          aggroLevel: m.aiState.aggroLevel,
          visionRange: m.aiState.visionRange,
        },
        isFloorBoss: m.isFloorBoss,
        isElite: m.isElite,
        aiEnabled: m.aiEnabled,
        aiLocked: m.aiLocked,
        dormantRoomIndex: m.dormantRoomIndex,
        activeBuffs: serializeBuffs(m),
        spellStates: m.getSpellStatesSnapshot(),
        activePhaseIndex: m.getActivePhaseIndex(),
      }));

    const groundItems: SavedGroundItem[] = dungeonService.inventoryService
      .getAllGroundItems()
      .map((gi) => ({
        itemId: gi.itemId,
        x: gi.x,
        y: gi.y,
        quantity: gi.quantity,
        mode: gi.mode,
        dropperId: gi.dropperId,
      }));

    let coop: SaveData['coop'];
    let coopPlayers: SavedCoopPlayer[] | undefined;
    if (netService.isHost() && netService.session) {
      const code = useNetSessionStore.getState().code;
      if (code) {
        coop = { code, roomId: netService.session.roomId() };
      }
      const localId = dungeonService.localPlayerId;
      const out: SavedCoopPlayer[] = [];
      for (const p of dungeonService.getPlayers()) {
        if (p.id === localId) {
          continue;
        }
        const token = reconnectRegistry.tokenForPlayer(p.id);
        const loadout = dungeonService.remoteLoadouts.get(p.id);
        if (!token || !loadout) {
          continue;
        }
        out.push({ playerId: p.id, token, loadout, ...serializePlayer(p) });
      }
      for (const id of coopDeathHost.downedPlayerIds()) {
        if (id === localId || out.some((e) => e.playerId === id)) {
          continue;
        }
        const token = reconnectRegistry.tokenForPlayer(id);
        const loadout = dungeonService.remoteLoadouts.get(id);
        if (!token || !loadout) {
          continue;
        }
        out.push({ playerId: id, token, loadout, downed: true });
      }
      coopPlayers = out.length > 0 ? out : undefined;
    }

    const data: SaveData = {
      version: SAVE_VERSION,
      timestamp: Date.now(),
      map: {
        style: this._mapData.style,
        width: this._mapData.width,
        height: this._mapData.height,
        tiles: this._mapData.tiles.map((t) => ({
          x: t.x,
          y: t.y,
          type: t.type,
        })),
        rooms: this._mapData.rooms,
        placedLocations: this._mapData.placedLocations,
        usedLocationKeys: dungeonService.locationService.getUsedLocationKeys(),
        destroyedLocationKeys:
          dungeonService.locationService.getDestroyedLocationKeys(),
      },
      player: serializePlayer(player),
      monsters,
      groundItems,
      discoveredTiles: mapService.getDiscoveredTilesArray(),
      currentFloor: useGameStore.getState().currentFloor,
      currentDungeonId: useGameStore.getState().currentDungeonId ?? undefined,
      dungeonRunConfig: useGameStore.getState().dungeonRunConfig ?? undefined,
      runUsedLocationIds: dungeonService.getRunUsedLocationIds(),
      usedByPlayer: dungeonService.locationService.getUsedByPlayer(),
      sockets: useSocketsStore.getState().socketed,
      coop,
      coopPlayers,
    };

    localStorage.setItem(SAVE_KEY, JSON.stringify(data));
  }

  // -- Load ----------------------------------------------------------------

  hasSave(): boolean {
    return localStorage.getItem(SAVE_KEY) !== null;
  }

  load(): SaveData | null {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) {
      return null;
    }
    try {
      const data: SaveData = JSON.parse(raw);
      if (data.version !== SAVE_VERSION) {
        return null;
      }
      return data;
    } catch {
      return null;
    }
  }

  /** Restore entities & map from a SaveData object. Call BEFORE dungeonService.initialize(). */
  restoreFromSave(data: SaveData): {
    tiles: ITile[];
    width: number;
    height: number;
    rooms: IRoom[];
  } {
    mapService.setDiscoveredTiles(data.discoveredTiles);

    const tiles: ITile[] = data.map.tiles.map((t, i) => ({
      id: i,
      x: t.x,
      y: t.y,
      type: t.type,
    }));

    this._mapData = {
      style: data.map.style,
      width: data.map.width,
      height: data.map.height,
      tiles,
      rooms: data.map.rooms,
      placedLocations: data.map.placedLocations,
    };

    const player = buildPlayerFromSavedPlayer(data.player);
    dungeonService.addEntity(player);
    useSocketsStore.getState().restore(data.sockets ?? {});
    dungeonService.buildPlayerRunEffects(player);
    player.recomputeStats();

    // Restore monsters
    for (const m of data.monsters) {
      const baseConfig = MONSTER_CONFIGS[m.configId];
      const monster = new MonsterEntity({
        life: m.life,
        maxLife: m.maxLife,
        isWalkableOver: false,
        knockbackable: baseConfig?.knockbackable ?? true,
        configId: m.configId,
        x: m.positionX,
        y: m.positionY,
        // Rebuild from baseStats (unbuffed) so restoreActiveBuffs + recompute
        // re-apply buff modifiers exactly once; using the computed `stats` here
        // would double-count them every save/load (e.g. elite size growth).
        stats: m.baseStats ?? m.stats,
        shield: m.shield,
        level: m.level ?? 1,
      });
      // Older saves lack `activeBuffs` — fall back to config-driven init to
      // avoid spawning a monster with no buffs at all.
      if (m.activeBuffs) {
        monster.restoreActiveBuffs(deserializeBuffs(m.activeBuffs));
      } else {
        monster.initializeBuffs();
      }
      monster.direction = m.direction ?? monster.direction;
      monster.aiState = {
        ...monster.aiState,
        behaviorState: m.aiState.behaviorState,
        aggroLevel: m.aiState.aggroLevel,
        visionRange: m.aiState.visionRange,
      };
      monster.isFloorBoss = m.isFloorBoss ?? false;
      if (m.isElite) {
        // Re-apply the elite marker only. The scalar scaling (attack, life…) is
        // baked into `baseStats` by `makeElite`, and the size bonus comes from
        // the `elite-aura` buff already restored via `activeBuffs` — so we must
        // NOT call `makeElite` again here (it would re-bake + re-add the buff).
        monster.isElite = true;
        const auraDef = BUFF_REGISTRY['elite-aura'];
        if (
          auraDef &&
          !monster.getActiveBuffs().some((b) => b.def.id === 'elite-aura')
        ) {
          monster.addBuff({ def: auraDef, source: 'elite' });
        }
      }
      if (m.spellStates) {
        monster.restoreSpellStates(m.spellStates);
      }
      if (m.activePhaseIndex !== undefined) {
        monster.restorePhaseIndex(m.activePhaseIndex);
      }
      monster.aiEnabled = m.aiEnabled ?? true;
      monster.aiLocked = m.aiLocked ?? false;
      monster.dormantRoomIndex = m.dormantRoomIndex ?? -1;
      dungeonService.addEntity(monster);
    }

    for (const gi of data.groundItems) {
      dungeonService.inventoryService.dropItem(
        gi.x,
        gi.y,
        gi.itemId,
        gi.quantity,
        false,
        gi.mode ?? 'possessed',
        gi.dropperId,
      );
    }

    return {
      tiles,
      width: data.map.width,
      height: data.map.height,
      rooms: data.map.rooms,
    };
  }

  /** Host resume: rebuild remote players (held for reconnect), seed the reconnect
   *  registry + remote loadouts + run-player set + downed set. Call AFTER
   *  restoreFromSave and AFTER the session is (re)hosted (localPlayerId set). */
  restoreCoopState(data: SaveData, departed: Set<string> = new Set()): void {
    // `coopPlayers` may be absent when the run was saved before any remote was
    // spawned (initial floor-entry checkpoint during loading) — still rebind the
    // host below so it's in `runPlayerIds`; the remote loop just no-ops.
    if (!data.coop) {
      return;
    }
    // Start from a clean host death-state: restoreDowned() augments the set, so a
    // stale downed/pendingDeath entry from a prior run must not survive.
    coopDeathHost.reset();
    // Bind the restored host avatar to the new host PeerId (re-host gave a fresh
    // localPeerId); without this, host intents/snapshots route to the wrong id.
    const hostId = dungeonService.localPlayerId;
    const hostPlayer = dungeonService.getPlayers()[0];
    if (hostId && hostPlayer) {
      hostPlayer.id = hostId;
      dungeonService.runPlayerIds.add(hostId);
    }
    const downedToRestore: string[] = [];
    for (const scp of data.coopPlayers ?? []) {
      // Left the run while we were down (relayed by the signaling server at
      // re-host): don't recreate or hold this avatar.
      if (departed.has(scp.token)) {
        continue;
      }
      reconnectRegistry.registerToken(scp.token, scp.playerId);
      dungeonService.remoteLoadouts.set(scp.playerId, scp.loadout);
      dungeonService.runPlayerIds.add(scp.playerId);
      if (scp.downed) {
        downedToRestore.push(scp.playerId);
        continue;
      }
      const player = buildPlayerFromSavedPlayer(scp, scp.loadout);
      player.id = scp.playerId;
      player.disconnected = true;
      player.remote = true;
      player.masteryLevels = scp.loadout.mastery ?? {};
      dungeonService.buildPlayerRunEffects(player);
      dungeonService.addEntity(player);
    }
    coopDeathHost.restoreDowned(downedToRestore);
  }

  /**
   * Clears the in-run checkpoint and drops the cached map data, so a stray
   * autosave fired during the exit transition (player position change while the
   * dungeon is still mounted) cannot re-create `SAVE_KEY` — which would block
   * launching a new run and resume the old dungeon on reload.
   */
  clearRunSave(): void {
    localStorage.removeItem(SAVE_KEY);
    this._mapData = null;
  }
}

export const saveService = SaveService.getInstance();
